/**
 * The plain-printing rule (Runebound `public.plain_printing`, migration 0036): the art used for a deck
 * card that names no printing. Same ordering as the SQL: no promo, no signature, not overnumbered, not
 * alt art, oldest set (unknown release dates last), then set code, number and suffix.
 *
 * The importer stores the database's own answer as `CatalogCard.plainPrintingId`; this function
 * reproduces it (a test checks they agree for every card) for data built elsewhere.
 */
import type { CatalogPrinting, CatalogSet } from './schema';

function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareFlags(a: boolean, b: boolean): number {
  return Number(a) - Number(b);
}

/** Comparator implementing the `plain_printing` ORDER BY. */
export function comparePlainness(
  a: CatalogPrinting,
  b: CatalogPrinting,
  releasedOn: (setCode: string) => string | null,
): number {
  const ra = releasedOn(a.setCode);
  const rb = releasedOn(b.setCode);
  return (
    compareFlags(a.isPromo, b.isPromo) ||
    compareFlags(a.isSignature, b.isSignature) ||
    compareFlags(a.isOvernumbered, b.isOvernumbered) ||
    compareFlags(a.isAltArt, b.isAltArt) ||
    (ra === rb ? 0 : ra === null ? 1 : rb === null ? -1 : compareStrings(ra, rb)) ||
    compareStrings(a.setCode, b.setCode) ||
    a.number - b.number ||
    compareStrings(a.suffix, b.suffix)
  );
}

/** The plainest printing among `printings` (all of one card), or `undefined`. */
export function plainestPrinting(
  printings: readonly CatalogPrinting[],
  sets: readonly CatalogSet[],
): CatalogPrinting | undefined {
  const released = new Map(sets.map((s) => [s.code, s.releasedOn]));
  const releasedOn = (code: string) => released.get(code) ?? null;
  return [...printings].sort((a, b) => comparePlainness(a, b, releasedOn))[0];
}
